import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useMeQuery } from "generated/graphql";

import { useSnack } from "hooks/useSnack";

import ChatPopup from "./components/ChatPopup";
import { useConversations, useMessageActions } from "../../messagesApi";

const POPUP_LIMIT = 3;

interface ChatPopupsApi {
  // Opens (and pins) a floating popup for the conversation, e.g. the
  // "pop out" button on the full-screen thread.
  popOut: (conversationId: number) => void;
}

const ChatPopupsContext = createContext<ChatPopupsApi>({ popOut: () => {} });

export const useChatPopups = () => useContext(ChatPopupsContext);

// "Message this vendor/customer" without leaving the current page: starts (or
// reopens) the conversation and pops it out as a floating chat. Counterpart to
// messagesApi's useMessageNavigation, which jumps to the messages page.
export const useMessagePopup = () => {
  const { t } = useTranslation("messages");
  const showSnack = useSnack();
  const { start } = useMessageActions();
  const { refresh } = useConversations();
  const { popOut } = useChatPopups();

  return useCallback(
    async (otherPartyId: number) => {
      const result = await start(otherPartyId);
      if (!result.ok || !result.conversationId) {
        showSnack(result.error ?? t("snack.start_failed"), "error");
        return;
      }
      // A just-created conversation isn't in the cached list yet — refresh so
      // the popup host can render it.
      refresh();
      popOut(result.conversationId);
    },
    [start, refresh, popOut, showSnack, t],
  );
};

interface PopupState {
  openIds: number[];
  pinnedIds: number[];
  minimizedIds: Set<number>;
}

interface PopupHostProps {
  state: PopupState;
  setState: React.Dispatch<React.SetStateAction<PopupState>>;
}

// Floating chat boxes. Two ways in: a conversation with unread messages
// auto-opens (except on the messages page, which already shows everything),
// and the thread view's "pop out" button pins one explicitly. Pinned popups
// follow the user everywhere until closed or reopened full-screen.
const PopupHost: React.FC<PopupHostProps> = ({ state, setState }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const { conversations } = useConversations();

  const onMessagesPage = location.pathname.startsWith("/dashboard/messages");

  // conversationId -> lastMessageAt when the popup was closed. ISO strings,
  // so a plain string compare orders them correctly.
  const dismissedRef = useRef(new Map<number, string>());

  useEffect(() => {
    if (onMessagesPage) {
      return;
    }
    setState((prev) => {
      let next = prev.openIds;
      for (const conversation of conversations) {
        if (conversation.unreadCount === 0 || conversation.archived) {
          continue;
        }
        if (next.includes(conversation.id) || next.length >= POPUP_LIMIT) {
          continue;
        }
        const dismissedAt = dismissedRef.current.get(conversation.id);
        if (dismissedAt && (conversation.lastMessageAt ?? "") <= dismissedAt) {
          continue;
        }
        next = [...next, conversation.id];
      }
      return next === prev.openIds ? prev : { ...prev, openIds: next };
    });
  }, [conversations, onMessagesPage, setState]);

  const toggleMinimize = (conversationId: number) =>
    setState((prev) => {
      const minimizedIds = new Set(prev.minimizedIds);
      if (minimizedIds.has(conversationId)) {
        minimizedIds.delete(conversationId);
      } else {
        minimizedIds.add(conversationId);
      }
      return { ...prev, minimizedIds };
    });

  const remove = (prev: PopupState, conversationId: number): PopupState => {
    const minimizedIds = new Set(prev.minimizedIds);
    minimizedIds.delete(conversationId);
    return {
      openIds: prev.openIds.filter((id) => id !== conversationId),
      pinnedIds: prev.pinnedIds.filter((id) => id !== conversationId),
      minimizedIds,
    };
  };

  const close = (conversationId: number, lastMessageAt?: string | null) => {
    dismissedRef.current.set(
      conversationId,
      lastMessageAt ?? new Date().toISOString(),
    );
    setState((prev) => remove(prev, conversationId));
  };

  // The popup "moves back" into the full messenger: no dismissal marker, so
  // it can auto-reopen later if the thread is left unread.
  const openFull = (conversationId: number) => {
    setState((prev) => remove(prev, conversationId));
    navigate(`/dashboard/messages?c=${conversationId}`);
  };

  // On the messages page only explicitly popped-out chats stay visible.
  const openHere = onMessagesPage
    ? state.openIds.filter((id) => state.pinnedIds.includes(id))
    : state.openIds;

  // Three 320px popups in a row anchored right would put two of them past the
  // left edge of a phone, where `position: fixed` puts them out of reach.
  const visibleIds = isCompact ? openHere.slice(-1) : openHere;

  if (visibleIds.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        // Clears the estimator's bottom action bars, which are 80px tall and
        // only appear once the layout stacks.
        bottom: { xs: 96, md: 16 },
        right: 16,
        display: "flex",
        alignItems: "flex-end",
        gap: 2,
        zIndex: (theme) => theme.zIndex.drawer - 1,
      }}
    >
      {visibleIds.map((id) => {
        const conversation = conversations.find((c) => c.id === id);
        if (!conversation) {
          return null;
        }
        return (
          <ChatPopup
            key={id}
            conversation={conversation}
            minimized={state.minimizedIds.has(id)}
            onToggleMinimize={() => toggleMinimize(id)}
            onClose={() => close(id, conversation.lastMessageAt)}
            onOpenFull={() => openFull(id)}
          />
        );
      })}
    </Box>
  );
};

// Provider wrapping the app: owns the popup state so pinned chats survive
// route changes, and exposes popOut() to the messages page. Renders the
// floating stack only for logged-in users (nothing to poll otherwise).
const ChatPopups: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [{ data }] = useMeQuery();

  const [state, setState] = useState<PopupState>({
    openIds: [],
    pinnedIds: [],
    minimizedIds: new Set(),
  });

  const popOut = useCallback((conversationId: number) => {
    setState((prev) => {
      const minimizedIds = new Set(prev.minimizedIds);
      minimizedIds.delete(conversationId);
      return {
        openIds: prev.openIds.includes(conversationId)
          ? prev.openIds
          : [...prev.openIds, conversationId],
        pinnedIds: prev.pinnedIds.includes(conversationId)
          ? prev.pinnedIds
          : [...prev.pinnedIds, conversationId],
        minimizedIds,
      };
    });
  }, []);

  const api = useMemo(() => ({ popOut }), [popOut]);

  return (
    <ChatPopupsContext.Provider value={api}>
      {children}
      {data?.me && <PopupHost state={state} setState={setState} />}
    </ChatPopupsContext.Provider>
  );
};

export default ChatPopups;
